import React, { createContext, useContext, useReducer, useRef, useCallback, ReactNode } from 'react';
import { photographerAPI } from '@services/photographerApi';
import type {
  PhotographerEvent,
  EventsContextType,
  CreateEventPayload,
} from '@/types/photographer';
import type { PaginationMeta } from '@/types';

interface EventsState {
  events: PhotographerEvent[];
  currentEvent: PhotographerEvent | null;
  pagination: PaginationMeta | null;
  loading: boolean;
  error: string | null;
  filters: {
    search: string;
    status: string | null;
    dateRange: [Date, Date] | null;
    sortBy: 'created' | 'eventDate' | 'title' | 'revenue';
    sortOrder: 'asc' | 'desc';
  };
}

type EventsAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: { events: PhotographerEvent[]; pagination?: PaginationMeta } }
  | { type: 'FETCH_FAILURE'; payload: string }
  | { type: 'SET_CURRENT_EVENT'; payload: PhotographerEvent }
  | { type: 'ADD_EVENT'; payload: PhotographerEvent }
  | { type: 'UPDATE_EVENT'; payload: { eventId: string; updates: Partial<PhotographerEvent> } }
  | { type: 'DELETE_EVENT'; payload: string }
  | { type: 'SET_FILTERS'; payload: Partial<EventsState['filters']> }
  | { type: 'CLEAR_CACHE' }
  | { type: 'CLEAR_ERROR' };

const initialState: EventsState = {
  events: [],
  currentEvent: null,
  pagination: null,
  loading: false,
  error: null,
  filters: {
    search: '',
    status: null,
    dateRange: null,
    sortBy: 'created',
    sortOrder: 'desc',
  },
};

const eventsReducer = (state: EventsState, action: EventsAction): EventsState => {
  switch (action.type) {
    case 'FETCH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };

    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
        events: action.payload.events,
        pagination: action.payload.pagination || null,
        error: null,
      };

    case 'FETCH_FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case 'SET_CURRENT_EVENT':
      return {
        ...state,
        currentEvent: action.payload,
      };

    case 'ADD_EVENT':
      return {
        ...state,
        events: [action.payload, ...state.events],
      };

    case 'UPDATE_EVENT':
      return {
        ...state,
        events: state.events.map(event =>
          event.id === action.payload.eventId ? { ...event, ...action.payload.updates } : event
        ),
        currentEvent:
          state.currentEvent?.id === action.payload.eventId
            ? { ...state.currentEvent, ...action.payload.updates }
            : state.currentEvent,
      };

    case 'DELETE_EVENT':
      return {
        ...state,
        events: state.events.filter(event => event.id !== action.payload),
        currentEvent: state.currentEvent?.id === action.payload ? null : state.currentEvent,
      };

    case 'SET_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
      };

    case 'CLEAR_CACHE':
      return {
        ...state,
        events: [],
        currentEvent: null,
        pagination: null,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

const EventsContext = createContext<EventsContextType | null>(null);

interface EventsProviderProps {
  children: ReactNode;
}

export const PhotographerEventsProvider: React.FC<EventsProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(eventsReducer, initialState);

  // Latest filters via ref so fetchEvents keeps a stable identity. Without
  // this, fetchEvents was recreated every render; EventsManagement's
  // useEffect(() => fetchEvents(), [fetchEvents]) then re-fired on each
  // FETCH_START-driven re-render, flooding /photographer/events until the
  // browser ran out of sockets (ERR_INSUFFICIENT_RESOURCES) and the page
  // never left its loading state.
  const filtersRef = useRef(state.filters);
  filtersRef.current = state.filters;

  const fetchEvents = useCallback(async (
    filters?: Partial<EventsState['filters']> & { page?: number; limit?: number }
  ): Promise<void> => {
    dispatch({ type: 'FETCH_START' });

    try {
      const queryFilters = filters || filtersRef.current;
      const response = await photographerAPI.getEvents({
        page: filters?.page || 1,
        limit: filters?.limit || 100,
        search: queryFilters.search || undefined,
        status: queryFilters.status || undefined,
        sortBy: queryFilters.sortBy,
        sortOrder: queryFilters.sortOrder,
        dateFrom: queryFilters.dateRange?.[0]?.toISOString(),
        dateTo: queryFilters.dateRange?.[1]?.toISOString(),
      });

      dispatch({
        type: 'FETCH_SUCCESS',
        payload: { events: response.data, pagination: response.pagination },
      });
    } catch (error: any) {
      dispatch({
        type: 'FETCH_FAILURE',
        payload: error.message || 'Failed to fetch events',
      });
      throw error;
    }
  }, []);

  const fetchEvent = useCallback(async (eventId: string): Promise<PhotographerEvent> => {
    dispatch({ type: 'FETCH_START' });

    try {
      const response = await photographerAPI.getEvent(eventId);
      const event = response.data;

      dispatch({
        type: 'SET_CURRENT_EVENT',
        payload: event,
      });

      return event;
    } catch (error: any) {
      dispatch({
        type: 'FETCH_FAILURE',
        payload: error.message || 'Failed to fetch event',
      });
      throw error;
    }
  }, []);

  const createEvent = useCallback(async (eventData: CreateEventPayload): Promise<PhotographerEvent> => {
    dispatch({ type: 'FETCH_START' });

    try {
      const response = await photographerAPI.createEvent(eventData);
      const newEvent = response.data;

      dispatch({
        type: 'ADD_EVENT',
        payload: newEvent,
      });

      return newEvent;
    } catch (error: any) {
      dispatch({
        type: 'FETCH_FAILURE',
        payload: error.message || 'Failed to create event',
      });
      throw error;
    }
  }, []);

  const updateEvent = useCallback(async (
    eventId: string,
    updates: Partial<PhotographerEvent>
  ): Promise<PhotographerEvent> => {
    dispatch({ type: 'FETCH_START' });

    try {
      const response = await photographerAPI.updateEvent(eventId, updates);
      const updatedEvent = response.data;

      dispatch({
        type: 'UPDATE_EVENT',
        payload: { eventId, updates: updatedEvent },
      });

      return updatedEvent;
    } catch (error: any) {
      dispatch({
        type: 'FETCH_FAILURE',
        payload: error.message || 'Failed to update event',
      });
      throw error;
    }
  }, []);

  const deleteEvent = useCallback(async (eventId: string): Promise<void> => {
    dispatch({ type: 'FETCH_START' });

    try {
      await photographerAPI.deleteEvent(eventId);

      dispatch({
        type: 'DELETE_EVENT',
        payload: eventId,
      });
    } catch (error: any) {
      dispatch({
        type: 'FETCH_FAILURE',
        payload: error.message || 'Failed to delete event',
      });
      throw error;
    }
  }, []);

  const setFilters = useCallback((filters: Partial<EventsState['filters']>): void => {
    dispatch({
      type: 'SET_FILTERS',
      payload: filters,
    });
  }, []);

  const clearCache = useCallback((): void => {
    dispatch({ type: 'CLEAR_CACHE' });
  }, []);

  const value: EventsContextType = {
    ...state,
    fetchEvents,
    fetchEvent,
    createEvent,
    updateEvent,
    deleteEvent,
    setFilters,
    clearCache,
  };

  return <EventsContext.Provider value={value}>{children}</EventsContext.Provider>;
};

export const usePhotographerEvents = (): EventsContextType => {
  const context = useContext(EventsContext);
  if (!context) {
    throw new Error('usePhotographerEvents must be used within PhotographerEventsProvider');
  }
  return context;
};
